import { Card, Typography, Form, Input, InputNumber, Select, DatePicker, Button, Row, Col, Flex, Space, Tag, Divider, Switch, Upload } from 'antd'
import React, { useEffect, useState } from 'react'
import {
    ArrowLeftOutlined, UserOutlined, FileTextOutlined, CalendarOutlined,
    TeamOutlined, WalletOutlined, SettingOutlined, PaperClipOutlined,
    InboxOutlined,
} from "@ant-design/icons"
import { QuotationService } from '../../../Services/Manage/QuotationService'
import { InvoiceService } from '../../../Services/Manage/InvoiceService'
import { UserService } from '../../../Services/Manage/UserService'
import { DepartmentEmpServices } from '../../../Services/Manage/DepartmentEmpServices'
import { ProjectService } from '../../../Services/Manage/ProjectService'
import { ConfigurationService } from '../../../Services/Manage/ConfigurationService'
import { ServicetypeService } from '../../../Services/Manage/ServicetypeService'
import { ENDPOINT } from '../../../Services/ENDPOINT'
import useMessage from 'antd/es/message/useMessage'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'

const { TextArea } = Input
const { Option, OptGroup } = Select;
const { Title } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }

  .ant-select-disabled .ant-select-selector,
  .ant-input[disabled],
  .ant-input-number-disabled,
  .ant-input-number-disabled .ant-input-number-input,
  .ant-picker.ant-picker-disabled,
  textarea[disabled] {
    background-color: #fff !important;
    color: rgba(0, 0, 0, 0.88) !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.88) !important;
  }

  .ant-select-disabled .ant-select-selection-item {
    color: rgba(0, 0, 0, 0.88) !important;
  }

  .ant-upload.ant-upload-drag {
    background-color: #fff !important;
  }
`;

const CardTitle = ({ icon, no, text }) => (
    <span
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            color: "rgba(14, 93, 146, 0.95)",
        }}
    >
        {icon}
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const priorityOptions = [
    { value: "Low", label: "Low" },
    { value: "Medium", label: "Medium" },
    { value: "High", label: "High" },
    { value: "Urgent", label: "Urgent" },
];

const statusOptions = [
    { value: "Live", label: "Live" },
    { value: "Hold", label: "Hold" },
    { value: "Pending", label: "Pending" },
    { value: "Archive", label: "Archive" },
    { value: "Completed", label: "Completed" },
];

const paymentTypeOptions = [
    { value: "Milestone Based", label: "Milestone Based" },
    { value: "Fixed", label: "Fixed" },
    { value: "Hourly", label: "Hourly" },
];

const ViewProjectOverview = () => {
    const [messageApi, msgContext] = useMessage();
    const [form] = Form.useForm();
    const [activeQuotation, setActiveQuotation] = useState([]);
    const [directInvoices, setDirectInvoices] = useState([]);
    const [departmentOption, setDepartmentOption] = useState([]);
    const [projectTypeOptions, setProjectTypeOptions] = useState([]);
    const [currencyOptions, setCurrencyOptions] = useState([]);
    const [userList, setUserList] = useState([]);
    const [managerOption, setManagerOption] = useState([]);
    const [allManagers, setAllManagers] = useState([]);
    const [engineerOption, setEnginerOption] = useState([]);
    const [editProject, setEditProject] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [projectStatusOptions, setProjectStatusOptions] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();
    const id = location.state?.id;

    // This page is always view-only — reached only from Overview Project's View button.
    const readOnly = true;

    const source = Form.useWatch('pro_source', form);
    const startDate = Form.useWatch('pro_start_date', form);
    const endDate = Form.useWatch('pro_target_end_date', form);

    const durationDays = (startDate && endDate)
        ? Math.max(dayjs(endDate).diff(dayjs(startDate), 'day'), 0)
        : 0;

    useEffect(() => {
        if (id) {
            getAllUser().then((activeUsers) => getProjectDataByID(id, activeUsers));
        }
    }, [id]);

    const getProjectDataByID = async (id, activeUsers = allManagers) => {
        try {
            const request = await ProjectService.getAllProjectById(id);
            const initialValues = request.data;
            setEditProject(initialValues);

            let proEngineer = initialValues.pro_engineer;
            if (typeof proEngineer === 'string') {
                try { proEngineer = JSON.parse(proEngineer); } catch { proEngineer = []; }
            }

            let proTags = initialValues.pro_tags;
            if (typeof proTags === 'string') {
                try { proTags = JSON.parse(proTags); } catch { proTags = []; }
            }

            let proAttachments = initialValues.pro_attachments;
            if (typeof proAttachments === 'string') {
                try { proAttachments = JSON.parse(proAttachments); } catch { proAttachments = []; }
            }
            setFileList((proAttachments || []).map((f, idx) => ({
                uid: `existing-${idx}`,
                name: f.filename || f.name,
                status: 'done',
                url: f.path?.startsWith('http') ? f.path : `${ENDPOINT.base}/${f.path}`,
                response: { file: f },
            })));

            setSelectedDepartment(initialValues.pro_department);
            handleGetEnginer(initialValues.pro_department, activeUsers);

            form.setFieldsValue({
                ...initialValues,
                pro_department: String(initialValues.pro_department),
                pro_engineer: proEngineer,
                pro_tags: proTags || [],
                pro_start_date: initialValues?.pro_start_date ? dayjs(initialValues.pro_start_date, 'DD-MM-YYYY') : null,
                pro_target_end_date: initialValues?.pro_target_end_date ? dayjs(initialValues.pro_target_end_date, 'DD-MM-YYYY') : null,
                pro_target_completion_date: initialValues?.pro_target_completion_date ? dayjs(initialValues.pro_target_completion_date) : null,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const getAllQotationProjects = async () => {
        try {
            const request = await QuotationService.getAllQuotation();
            setActiveQuotation(
                (request.data || []).filter((q) => q.converted_to_project === true)
            );
        } catch (error) {
            console.error(error);
        }
    };

    const getAllDirectInvoices = async () => {
        try {
            const request = await InvoiceService.getAllInvoice();
            const rows = (request?.data || []).filter(
                (inv) => (inv.source || "quotation") === "manual" && !inv.project_created
            );
            setDirectInvoices(rows);
        } catch (error) {
            console.error(error);
        }
    };

    const getAllUser = async () => {
        try {
            const request = await UserService.getAllUsers();
            setUserList(request.data);
            const active = (request.data || []).map((u) => ({
                value: u.user_id,
                label: `${u.name}${u.user_id ? ` (${u.user_id})` : ""}`,
                department_id: String(u.department_id || ""),
            }));
            setAllManagers(active);
            setManagerOption(active);
            return active;
        } catch (error) {
            console.error(error);
            return [];
        }
    };

    const getAllDepartment = async () => {
        try {
            const request = await DepartmentEmpServices.getAllDepartment();
            const option = request.data.map((item) => ({ value: String(item.id), label: item.name }));
            setDepartmentOption(option);
            return option;
        } catch (error) {
            console.error(error);
            return [];
        }
    };
    const getProjectTypes = async () => {
        try {
            const request = await ConfigurationService.getByType('project_type');
            const option = (request.data || []).map((item) => ({ value: item.label, label: item.label }));
            setProjectTypeOptions(option);
        } catch (error) {
            console.error(error);
        }
    };

    const getProjectStatusOptions = async () => {
        try {
            const request = await ConfigurationService.getByType('project_status');
            const option = (request.data || []).map((item) => ({ value: item.label, label: item.label }));
            setProjectStatusOptions(option);
        } catch (error) {
            console.error(error);
        }
    };

    const getCurrencyOptions = async () => {
        try {
            const request = await ServicetypeService.getAllCurrency();
            const option = (request.data || []).map((item) => ({
                value: item.code,
                label: `${item.code} - ${item.name}`,
                id: item.id,
            }));
            setCurrencyOptions(option);
        } catch (error) {
            console.error(error);
        }
    };

    const [departmentNameMap, setDepartmentNameMap] = useState({});

    useEffect(() => {
        getAllQotationProjects();
        getAllDirectInvoices();
        getAllDepartment().then((opts) => {
            const map = {};
            opts.forEach((o) => { map[o.value] = o.label; });
            setDepartmentNameMap(map);
        });
        getAllUser();
        getProjectTypes();
        getProjectStatusOptions();
        getCurrencyOptions();
    }, []);

    const handleGetEnginer = (value, sourceList = allManagers) => {
        setSelectedDepartment(value);

        if (value) {
            const filtered = sourceList.filter((u) =>
                (u.department_id || "").split(",").some((did) => did.trim() === String(value))
            );
            setManagerOption(filtered);
            setEnginerOption(filtered);
        } else {
            setManagerOption(sourceList);
            setEnginerOption(sourceList);
        }
    };

    const renderItem = (eqId, customer_name, customer_company) => (
        <Flex align="center" justify="space-between" style={{ width: "100%" }}>
            <span>{eqId}</span>
            <span><UserOutlined /> {customer_name || customer_company}</span>
        </Flex>
    );

    const grouped = activeQuotation.reduce((acc, item) => {
        if (!acc[item.service_id]) {
            acc[item.service_id] = { service_name: item.service_name, items: [] };
        }
        acc[item.service_id].items.push(item);
        return acc;
    }, {});

    return (
        <>
            <style>{compactFormStyle}</style>
            {msgContext}
            <Card
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined
                                style={{ cursor: "pointer", fontSize: "20px" }}
                                onClick={() => navigate("/overview-project")}
                            />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    View Project{' '}
                                    {editProject && (
                                        <Tag color="cyan" style={{ fontSize: '16px', padding: '6px 16px', borderRadius: '8px', fontWeight: 600 }}>
                                            Project ID: {editProject.pro_no}
                                        </Tag>
                                    )}
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    <Space>
                        <Button size="middle" disabled={false} onClick={() => navigate("/overview-project")}>
                            Back
                        </Button>
                        <Button
                            size="middle"
                            disabled={false}
                            onClick={() =>
                                navigate('/overview-project/requirement-gathering', {
                                    state: {
                                        id,
                                        pro_name: editProject?.pro_name,
                                        pro_no: editProject?.pro_no,
                                        pro_customer: editProject?.pro_customer,
                                        pro_manager: editProject?.pro_manager,
                                        pro_manager_id: editProject?.pro_manager,
                                        pro_value: editProject?.pro_value,
                                        pro_engineer: editProject?.pro_engineer,
                                        pro_start_date: editProject?.pro_start_date,
                                        pro_target_end_date: editProject?.pro_target_end_date,
                                        pro_status: editProject?.pro_status,
                                    },
                                })
                            }
                        >
                            Requirement Gathering
                        </Button>
                        <Button
                            size="middle"
                            type="primary"
                            disabled={false}
                            style={{
                                backgroundColor: "rgba(14, 93, 146, 0.95)",
                                borderColor: "rgba(14, 93, 146, 0.95)",
                                color: "#fff",
                            }}
                            onClick={() =>
                                navigate('/overview-project/milestones', {
                                    state: {
                                        id,
                                        pro_name: editProject?.pro_name,
                                        pro_no: editProject?.pro_no,
                                        pro_customer: editProject?.pro_customer,
                                        pro_manager: editProject?.pro_manager,
                                        pro_value: editProject?.pro_value,
                                        pro_engineer: editProject?.pro_engineer,
                                        pro_start_date: editProject?.pro_start_date,
                                        pro_target_end_date: editProject?.pro_target_end_date,
                                        pro_status: editProject?.pro_status,
                                        pro_department_name: departmentNameMap[String(editProject?.pro_department)],
                                    },
                                })
                            }
                        >
                            Milestones
                        </Button>
                        <Button
                            size="middle"
                            type="primary"
                            disabled={false}
                            style={{
                                backgroundColor: "rgba(14, 93, 146, 0.95)",
                                borderColor: "rgba(14, 93, 146, 0.95)",
                                color: "#fff",
                            }}
                            onClick={() =>
                                navigate('/overview-project/tasks', {
                                    state: {
                                        id,
                                        pro_name: editProject?.pro_name,
                                        pro_no: editProject?.pro_no,
                                        pro_customer: editProject?.pro_customer,
                                        pro_manager: editProject?.pro_manager,
                                        pro_value: editProject?.pro_value,
                                        pro_engineer: editProject?.pro_engineer,
                                        pro_start_date: editProject?.pro_start_date,
                                        pro_target_end_date: editProject?.pro_target_end_date,
                                        pro_status: editProject?.pro_status,
                                        pro_department_name: departmentNameMap[String(editProject?.pro_department)],
                                    },
                                })
                            }
                        >
                            Tasks
                        </Button>
                    </Space>
                }
            >
                <Form layout="vertical" form={form} size="middle" disabled={readOnly}>
                    <Card title={<CardTitle icon={<FileTextOutlined />} no={1} text="Project Information" />} style={{ marginBottom: 24 }}>
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pro_name" label="Project Name">
                                    <Input size="large" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item label="Project Code">
                                    <Input size="large" readOnly value={editProject ? editProject.pro_no : ""} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pro_source" label="Source">
                                    <Select size="large">
                                        <Option value="Quotation">Quotation</Option>
                                        <Option value="Manual">Manual</Option>
                                        <Option value="Direct Invoice">Direct Invoice</Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                {source === "Direct Invoice" ? (
                                    <Form.Item name="qu_no" label="Source Document No.">
                                        <Select size="large">
                                            {directInvoices.map((inv) => (
                                                <Option key={inv.invoice_no} value={inv.invoice_no}>
                                                    {renderItem(inv.invoice_no, inv.customer_contact_person, inv.customer_name)}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                ) : (
                                    <Form.Item name="qu_no" label="Source Document No.">
                                        <Select size="large">
                                            {Object.values(grouped).map((group) => (
                                                <OptGroup key={group.service_name} label={group.service_name}>
                                                    {group.items.map((item) => (
                                                        <Option key={item.qu_no} value={item.qu_no}>
                                                            {renderItem(item.qu_no, item.customer_name, item.customer_company)}
                                                        </Option>
                                                    ))}
                                                </OptGroup>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                )}
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pro_type" label="Project Type">
                                    <Select options={projectTypeOptions} size="large" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pro_customer" label="Customer / Company">
                                    <Input size="large" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pro_value" label="Project Value (₹)">
                                    <InputNumber size="large" style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pro_currency" label="Currency">
                                    <Select size="large" options={currencyOptions} />
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item name="pro_description" label="Project Description / Scope">
                                    <TextArea rows={3} size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card title={<CardTitle icon={<CalendarOutlined />} no={2} text="Project Timeline" />} style={{ marginBottom: 24 }}>
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pro_start_date" label="Start Date">
                                    <DatePicker size="large" style={{ width: "100%" }} format="DD-MM-YYYY" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pro_target_end_date" label="Expected End Date">
                                    <DatePicker size="large" style={{ width: "100%" }} format="DD-MM-YYYY" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item label="Duration">
                                    <Input size="large" readOnly value={`${durationDays} Days`} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item name="pro_target_completion_date" label="Target Completion Date">
                                    <DatePicker size="large" style={{ width: "100%" }} format="DD-MM-YYYY" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Row gutter={16} style={{ marginBottom: 10 }}>
                        <Col xs={24} lg={8}>
                            <Card title={<CardTitle icon={<TeamOutlined />} no={3} text="Project Assignment" />} style={{ height: '100%' }}>
                                <Row gutter={16}>
                                    <Col xs={16}>
                                        <Form.Item name="pro_department" label="Department">
                                            <Select size="large" options={departmentOption} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={8}>
                                        <Form.Item name="pro_team_size" label="Team Size">
                                            <InputNumber size="large" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="pro_manager" label="Project Manager">
                                            <Select options={managerOption} size="large" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="pro_engineer" label="Engineer">
                                            <Select size="large" mode="multiple" options={engineerOption} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="pro_priority" label="Priority">
                                            <Select size="large" options={priorityOptions} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col xs={24} lg={8}>
                            <Card title={<CardTitle icon={<WalletOutlined />} no={4} text="Project Budget & Payment" />} style={{ height: '100%' }}>
                                <Row gutter={16}>
                                    <Col xs={12}>
                                        <Form.Item name="pro_payment_type" label="Payment Type">
                                            <Select size="large" options={paymentTypeOptions} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Item name="pro_total_budget" label="Total Budget (₹)">
                                            <InputNumber size="large" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Item name="pro_down_payment_percent" label="Down Payment (%)">
                                            <InputNumber size="large" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Item name="pro_down_payment_amount" label="Down Payment Amount (₹)">
                                            <InputNumber size="large" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="pro_payment_terms" label="Payment Terms">
                                            <TextArea rows={3} size="large" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col xs={24} lg={8}>
                            <Card title={<CardTitle icon={<SettingOutlined />} no={5} text="Project Status & Settings" />} style={{ height: '100%' }}>
                                <Row gutter={16}>
                                    <Col xs={12}>
                                        <Form.Item name="pro_status" label="Initial Status">
                                            <Select size="large" options={statusOptions} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Item name="pro_visibility" label="Project Status">
                                            <Select size="large" options={projectStatusOptions} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="pro_allow_time_tracking" label="Allow Time Tracking" valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="pro_enable_task_management" label="Enable Task Management" valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="pro_enable_milestone_tracking" label="Enable Milestone Tracking" valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item name="pro_send_email_notifications" label="Send Email Notifications" valuePropName="checked">
                                            <Switch />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </Row>

                    <Card title={<CardTitle icon={<PaperClipOutlined />} no={6} text="Additional Information" />} style={{ marginBottom: 24 }}>
                        <Row gutter={16}>
                            <Col xs={24} md={8}>
                                <Form.Item name="pro_tags" label="Tags / Keywords">
                                    <Select mode="tags" size="large" open={false} suffixIcon={null} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Attachments">
                                    <Upload.Dragger fileList={fileList}>
                                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                        <p className="ant-upload-text">{fileList.length ? "" : "No files"}</p>
                                    </Upload.Dragger>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item name="pro_comment" label="Notes">
                                    <TextArea rows={5} size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Divider />
                    <div style={{ display: 'flex', justifyContent: 'right' }}>
                        <Space>
                            <Button size="large" disabled={false} onClick={() => navigate("/overview-project")}>
                                Back
                            </Button>
                            <Button
                                size="large"
                                disabled={false}
                                onClick={() =>
                                    navigate('/overview-project/requirement-gathering', {
                                        state: {
                                            id,
                                            pro_name: editProject?.pro_name,
                                            pro_no: editProject?.pro_no,
                                            pro_customer: editProject?.pro_customer,
                                            pro_manager: editProject?.pro_manager,
                                            pro_manager_id: editProject?.pro_manager,
                                            pro_value: editProject?.pro_value,
                                            pro_engineer: editProject?.pro_engineer,
                                            pro_start_date: editProject?.pro_start_date,
                                            pro_target_end_date: editProject?.pro_target_end_date,
                                            pro_status: editProject?.pro_status,
                                        },
                                    })
                                }
                            >
                                Requirement Gathering
                            </Button>
<Button
                                size="large"
                                type="primary"
                                disabled={false}
                                style={{
                                    backgroundColor: "rgba(14, 93, 146, 0.95)",
                                    borderColor: "rgba(14, 93, 146, 0.95)",
                                    color: "#fff",
                                }}
                                onClick={() =>
                                    navigate('/overview-project/milestones', {
                                        state: {
                                            id,
                                            pro_name: editProject?.pro_name,
                                            pro_no: editProject?.pro_no,
                                            pro_customer: editProject?.pro_customer,
                                            pro_manager: editProject?.pro_manager,
                                            pro_value: editProject?.pro_value,
                                            pro_engineer: editProject?.pro_engineer,
                                            pro_start_date: editProject?.pro_start_date,
                                            pro_target_end_date: editProject?.pro_target_end_date,
                                            pro_status: editProject?.pro_status,
                                            pro_department_name: departmentNameMap[String(editProject?.pro_department)],
                                        },
                                    })
                                }
                            >
                                Milestones
                            </Button>
                            <Button
                                size="large"
                                type="primary"
                                disabled={false}
                                style={{
                                    backgroundColor: "rgba(14, 93, 146, 0.95)",
                                    borderColor: "rgba(14, 93, 146, 0.95)",
                                    color: "#fff",
                                }}
                                onClick={() =>
                                    navigate('/overview-project/tasks', {
                                        state: {
                                            id,
                                            pro_name: editProject?.pro_name,
                                            pro_no: editProject?.pro_no,
                                            pro_customer: editProject?.pro_customer,
                                            pro_manager: editProject?.pro_manager,
                                            pro_value: editProject?.pro_value,
                                            pro_engineer: editProject?.pro_engineer,
                                            pro_start_date: editProject?.pro_start_date,
                                            pro_target_end_date: editProject?.pro_target_end_date,
                                            pro_status: editProject?.pro_status,
                                            pro_department_name: departmentNameMap[String(editProject?.pro_department)],
                                        },
                                    })
                                }
                            >
                                Tasks
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Card>
        </>
    );
};

export default ViewProjectOverview;